import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tooltip as TooltipUI, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { Download, TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight, Loader2, RefreshCw, AlertTriangle, Lightbulb, Building2, Calendar, X, FileSpreadsheet, CalendarDays, CheckSquare, ChevronDown, ChevronUp, Beaker, Play, Eye, Sliders, BarChart3, Plus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart, BarChart, Bar } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import BankAccountsPanel from '../components/BankAccountsPanel';
import { OverdueWarningCard } from '../components/OverdueWarningCard';
import PaymentStatsCard from '../components/PaymentStatsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Custom dot component to show red for negative values
const CustomDot = (props) => {
  const { cx, cy, payload } = props;
  const isNegative = payload.closing < 0;
  return (
    <circle 
      cx={cx} 
      cy={cy} 
      r={5} 
      fill={isNegative ? '#ef4444' : '#1e3a8a'} 
      stroke="white"
      strokeWidth={2}
    />
  );
};

export default function DashboardPage() {
  const { activeBusiness } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeOther, setIncludeOther] = useState(true);
  
  // Daily drill-down state
  const [dailyModalOpen, setDailyModalOpen] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  
  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState('monthly');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [exportFormat, setExportFormat] = useState('excel');
  const [exporting, setExporting] = useState(false);
  
  // Scenario testing state
  const [scenarioExpanded, setScenarioExpanded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [otherCashItems, setOtherCashItems] = useState([]);
  const [scenarioType, setScenarioType] = useState('global_ar');
  const [scenarioTarget, setScenarioTarget] = useState('');
  const [scenarioTargetIds, setScenarioTargetIds] = useState([]);
  const [scenarioDays, setScenarioDays] = useState(0);
  const [scenarioPreview, setScenarioPreview] = useState(null);
  const [scenarioLoading, setScenarioLoading] = useState(false);

  // Chart range selector state
  const [chartView, setChartView] = useState('daily'); // daily-only (monthly view removed)
  const [chartMonthCount, setChartMonthCount] = useState(13); // 1..13
  const [chartDailyStart, setChartDailyStart] = useState('');
  const [chartDailyEnd, setChartDailyEnd] = useState('');
  const [dailyRangeData, setDailyRangeData] = useState([]);
  const [dailyRangeLoading, setDailyRangeLoading] = useState(false);
  const [dailyRangeApplied, setDailyRangeApplied] = useState(false);

  // Negative days in next 90 days (for the compact Cash Flow Alert)
  const [negativeDays, setNegativeDays] = useState([]);
  const [negativeDaysLoading, setNegativeDaysLoading] = useState(false);

  // Invoices/Bills lists for scenario testing multi-select (May 10, 2026)
  const [invoicesList, setInvoicesList] = useState([]);
  const [billsList, setBillsList] = useState([]);
  // Multi-scenario staging
  const [stagedScenarios, setStagedScenarios] = useState([]);  // [{type,target_id,target_ids,days, label}]

  // Xero status for dashboard header sync button
  const [xeroStatus, setXeroStatus] = useState(null);
  const fetchXeroStatus = async () => {
    try {
      const r = await axios.get(`${API}/xero/status`);
      setXeroStatus(r.data || null);
    } catch (e) {
      setXeroStatus(null);
    }
  };

  // Cash-flow threshold (grey line on chart). Persisted in /settings.
  const [cashflowThreshold, setCashflowThreshold] = useState(0);
  const [thresholdDraft, setThresholdDraft] = useState('');
  const [thresholdSaving, setThresholdSaving] = useState(false);

  // Cash flow tables (Monthly + Daily) collapsed by default — user requested
  // "drop down when needed. otherwise keep it hidden"
  const [tablesOpen, setTablesOpen] = useState(false);
  const [tablesView, setTablesView] = useState('monthly'); // 'monthly' | 'daily'

  // Payment cycle averages (DSO/DPO) — populated from /api/dashboard/payment-stats
  const [paymentStats, setPaymentStats] = useState(null);
  const fetchPaymentStats = async () => {
    try {
      const r = await axios.get(`${API}/dashboard/payment-stats`);
      setPaymentStats(r.data || null);
    } catch (e) {
      setPaymentStats(null);
    }
  };
  const fetchThreshold = async () => {
    try {
      const r = await axios.get(`${API}/settings`);
      const t = Number(r.data?.cashflow_threshold ?? 0) || 0;
      setCashflowThreshold(t);
      setThresholdDraft(t ? String(t) : '');
    } catch (e) {
      // silent
    }
  };
  const saveThreshold = async () => {
    const numeric = parseFloat(thresholdDraft);
    if (thresholdDraft !== '' && Number.isNaN(numeric)) {
      toast.error('Threshold must be a number');
      return;
    }
    setThresholdSaving(true);
    try {
      const value = thresholdDraft === '' ? 0 : numeric;
      await axios.put(`${API}/settings`, { cashflow_threshold: value });
      setCashflowThreshold(value);
      toast.success(value > 0 ? `Threshold set to $${value.toLocaleString()}` : 'Threshold cleared');
    } catch (e) {
      toast.error('Failed to save threshold');
    } finally {
      setThresholdSaving(false);
    }
  };

  // Overdue items (invoices/bills due before today) — surfaced as a warning
  const [overdue, setOverdue] = useState(null);
  const fetchOverdue = async () => {
    try {
      const r = await axios.get(`${API}/overdue/summary`);
      setOverdue(r.data || null);
    } catch (e) {
      setOverdue(null);
    }
  };
  useEffect(() => {
    fetchXeroStatus();
    fetchOverdue();
    fetchThreshold();
    fetchPaymentStats();
  }, [activeBusiness]);

  // Fetch customers, suppliers and other cash for scenario testing
  const fetchScenarioData = async () => {
    try {
      const [customersRes, suppliersRes, otherCashRes, invoicesRes, billsRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/other-cash`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/bills`),
      ]);
      setCustomers(customersRes.data);
      setSuppliers(suppliersRes.data);
      setOtherCashItems(otherCashRes.data);
      setInvoicesList(invoicesRes.data || []);
      setBillsList(billsRes.data || []);
    } catch (error) {
      console.error('Failed to load scenario data');
    }
  };
  useEffect(() => {
    fetchScenarioData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusiness]);

  // Refresh the invoice/bill lists every time the scenario panel is expanded —
  // ensures manually edited extra_days / expected_payment_date show up without a
  // full page reload.
  useEffect(() => {
    if (scenarioExpanded) {
      fetchScenarioData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioExpanded]);

  useEffect(() => {
    fetchDashboard();
  }, [includeOther, activeBusiness]);

  // Also refresh when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboard();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      // Call the consolidated endpoint when the user has picked the
      // pseudo "All businesses" option; otherwise regular per-business endpoint.
      const url = activeBusiness?.is_consolidated
        ? `${API}/dashboard/consolidated`
        : `${API}/dashboard`;
      const response = await axios.get(url, {
        params: { include_other: includeOther }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch the next 90 days of daily forecast and surface dates with closing<0
  const fetchNegativeDays = async () => {
    setNegativeDaysLoading(true);
    try {
      const today = new Date();
      const months = [];
      for (let i = 0; i < 4; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
        months.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
      }
      const responses = await Promise.all(
        months.map((m) =>
          axios.get(`${API}/dashboard/daily/${m.year}/${m.month}`).catch(() => ({ data: { daily_data: [] } }))
        )
      );
      const cutoff = new Date(today);
      cutoff.setDate(cutoff.getDate() + 90);
      const todayISO = today.toISOString().slice(0, 10);
      const cutoffISO = cutoff.toISOString().slice(0, 10);
      const flagged = [];
      responses.forEach((res) => {
        (res.data?.daily_data || []).forEach((d) => {
          if (d.date >= todayISO && d.date <= cutoffISO && d.closing_cash < 0) {
            flagged.push({ date: d.date, closing_cash: d.closing_cash });
          }
        });
      });
      flagged.sort((a, b) => a.date.localeCompare(b.date));
      setNegativeDays(flagged);
    } catch (error) {
      console.error('Failed to fetch negative days', error);
    } finally {
      setNegativeDaysLoading(false);
    }
  };

  // Trigger Xero sync from the dashboard (replaces the need to go to Settings)
  const [xeroSyncing, setXeroSyncing] = useState(false);
  const handleXeroSync = async () => {
    setXeroSyncing(true);
    try {
      const res = await axios.post(`${API}/xero/sync`);
      const banks = res.data?.bank_accounts;
      toast.success(
        banks
          ? `Xero sync done · ${banks.bank_accounts_imported || 0} bank account(s) updated`
          : 'Xero sync done'
      );
      await Promise.all([fetchDashboard(), fetchNegativeDays(), fetchXeroStatus()]);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Xero sync failed — connect Xero in Settings first';
      toast.error(msg);
    } finally {
      setXeroSyncing(false);
    }
  };

  // Friendly "5m ago" / "2h ago" formatter for last_sync timestamps
  const formatRelativeTime = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const diff = Date.now() - d.getTime();
      if (Number.isNaN(diff)) return null;
      const m = Math.floor(diff / 60000);
      if (m < 1) return 'just now';
      if (m < 60) return `${m}m ago`;
      const h = Math.floor(m / 60);
      if (h < 24) return `${h}h ago`;
      const days = Math.floor(h / 24);
      return `${days}d ago`;
    } catch (_e) {
      return null;
    }
  };

  // Reload negative-days when dashboard data updates
  useEffect(() => {
    if (data?.monthly_data?.length) fetchNegativeDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.opening_cash]);

  // Fetch daily cash flow data for a specific month
  const fetchDailyData = async (year, month, monthName) => {
    setDailyLoading(true);
    setSelectedMonth({ year, month, name: monthName });
    setDailyModalOpen(true);
    try {
      const response = await axios.get(`${API}/dashboard/daily/${year}/${month}`);
      setDailyData(response.data);
    } catch (error) {
      toast.error('Failed to load daily cash flow data');
      console.error(error);
    } finally {
      setDailyLoading(false);
    }
  };

  // Handle double-click on month row
  const handleMonthDoubleClick = (month) => {
    const monthNum = MONTH_NAMES.indexOf(month.month) + 1;
    fetchDailyData(month.year, monthNum, month.month);
  };

  // Open export modal
  const openExportModal = (format) => {
    setExportFormat(format);
    setExportType('monthly');
    setSelectedMonths([]);
    setExportModalOpen(true);
  };

  // Toggle month selection
  const toggleMonthSelection = (monthKey) => {
    setSelectedMonths(prev => 
      prev.includes(monthKey) 
        ? prev.filter(m => m !== monthKey)
        : [...prev, monthKey]
    );
  };

  // Select/deselect all months
  const toggleAllMonths = () => {
    if (selectedMonths.length === data?.monthly_data?.length) {
      setSelectedMonths([]);
    } else {
      setSelectedMonths(data?.monthly_data?.map(m => `${m.year}-${m.month_num}`) || []);
    }
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      let endpoint = '';
      let filename = '';
      
      if (exportType === 'monthly') {
        // Export monthly summary
        endpoint = exportFormat === 'excel' ? `${API}/export/excel` : `${API}/export/csv`;
        filename = `cashflow_monthly_${data?.business_name?.replace(/\s+/g, '_') || 'export'}`;
      } else {
        // Export daily breakdown for selected months
        const monthsParam = selectedMonths.join(',');
        endpoint = exportFormat === 'excel' 
          ? `${API}/export/excel/daily?months=${monthsParam}`
          : `${API}/export/csv/daily?months=${monthsParam}`;
        filename = `cashflow_daily_${data?.business_name?.replace(/\s+/g, '_') || 'export'}`;
      }
      
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${exportFormat === 'excel' ? 'Excel' : 'CSV'} exported successfully`);
      setExportModalOpen(false);
    } catch (error) {
      toast.error(`Failed to export ${exportFormat === 'excel' ? 'Excel' : 'CSV'}`);
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  // Scenario types — group helpers
  const isInflowType = (t) => t === 'global_inflow' || t === 'inflow';
  const isOutflowType = (t) => t === 'global_outflow' || t === 'outflow';
  // "Pay AP Early" variants — UI-only types that map to the existing global_ap
  // / bills backend types with a negative `extra_days_offset` so the user
  // doesn't have to think about signs.
  const isAPEarlyType = (t) => t === 'global_ap_early' || t === 'bills_early';
  const isInvoiceBillType = (t) =>
    t === 'invoices' || t === 'bills' || t === 'bills_early';
  const isSpecificType = (t) =>
    ['inflow', 'outflow', 'invoices', 'bills', 'bills_early'].includes(t);

  // Resolve signed days actually sent to the API. AR/AP types pass the value
  // verbatim (signed input), while the four Other Cash types and AP-early
  // types accept a positive number from the user and the direction comes
  // from the type.
  const resolveSignedDays = () => {
    const raw = parseInt(scenarioDays, 10) || 0;
    if (isInflowType(scenarioType)) return -Math.abs(raw);
    if (isOutflowType(scenarioType)) return Math.abs(raw);
    if (isAPEarlyType(scenarioType)) return -Math.abs(raw);
    return raw;
  };

  // Build the payload sent to /scenario/preview and /scenario/apply.
  // AP-early variants are translated to their underlying backend type.
  const buildScenarioPayload = () => {
    const backendType =
      scenarioType === 'global_ap_early' ? 'global_ap'
      : scenarioType === 'bills_early' ? 'bills'
      : scenarioType;
    const payload = {
      adjustment_type: backendType,
      extra_days_offset: resolveSignedDays(),
    };
    if (isInvoiceBillType(scenarioType)) {
      payload.target_ids = scenarioTargetIds;
    } else if (scenarioTarget) {
      payload.target_id = scenarioTarget;
    }
    return payload;
  };

  // Scenario testing functions
  const handlePreviewScenario = async () => {
    const signed = resolveSignedDays();
    if (signed === 0) {
      toast.error('Please enter a non-zero days adjustment');
      return;
    }
    if (isInvoiceBillType(scenarioType) && scenarioTargetIds.length === 0) {
      toast.error(`Please pick at least one ${scenarioType === 'invoices' ? 'invoice' : 'bill'}`);
      return;
    }
    if (isSpecificType(scenarioType) && !isInvoiceBillType(scenarioType) && !scenarioTarget) {
      toast.error('Please select a target');
      return;
    }

    setScenarioLoading(true);
    try {
      const response = await axios.post(`${API}/scenario/preview`, {
        ...buildScenarioPayload(),
        is_preview: true,
      });
      setScenarioPreview(response.data);
    } catch (error) {
      toast.error('Failed to preview scenario');
    } finally {
      setScenarioLoading(false);
    }
  };

  const handleApplyScenario = async () => {
    if (!scenarioPreview) return;

    setScenarioLoading(true);
    try {
      await axios.post(`${API}/scenario/apply`, buildScenarioPayload());
      toast.success('Scenario applied successfully');
      setScenarioPreview(null);
      setScenarioDays(0);
      setScenarioTargetIds([]);
      fetchDashboard();
      try {
        const res = await axios.get(`${API}/other-cash`);
        setOtherCashItems(res.data);
      } catch (_) { /* non-blocking */ }
    } catch (error) {
      toast.error('Failed to apply scenario');
    } finally {
      setScenarioLoading(false);
    }
  };

  // Stage current form values as another scenario to combine; clears form
  const handleStageScenario = () => {
    const signed = resolveSignedDays();
    if (signed === 0) {
      toast.error('Enter a days value before adding');
      return;
    }
    if (isInvoiceBillType(scenarioType) && scenarioTargetIds.length === 0) {
      toast.error(`Pick at least one ${scenarioType === 'invoices' || scenarioType === 'bills_early' ? scenarioType.replace('_early', '') : 'item'}`);
      return;
    }
    if (isSpecificType(scenarioType) && !isInvoiceBillType(scenarioType) && !scenarioTarget) {
      toast.error('Select a target before adding');
      return;
    }
    const label = (() => {
      const dayStr = `${signed > 0 ? '+' : ''}${signed}d`;
      if (scenarioType === 'global_ar') return `All AR ${dayStr}`;
      if (scenarioType === 'global_ap') return `All AP ${dayStr}`;
      if (scenarioType === 'global_ap_early') return `All AP Early ${dayStr}`;
      if (scenarioType === 'global_inflow') return `All Inflows ${dayStr}`;
      if (scenarioType === 'global_outflow') return `All Outflows ${dayStr}`;
      if (scenarioType === 'invoices') return `${scenarioTargetIds.length} invoice(s) ${dayStr}`;
      if (scenarioType === 'bills') return `${scenarioTargetIds.length} bill(s) ${dayStr}`;
      if (scenarioType === 'bills_early') return `${scenarioTargetIds.length} bill(s) early ${dayStr}`;
      if (scenarioType === 'inflow') {
        const oc = otherCashItems.find((o) => o.id === scenarioTarget);
        return `Inflow${oc ? ` "${oc.category}"` : ''} ${dayStr}`;
      }
      if (scenarioType === 'outflow') {
        const oc = otherCashItems.find((o) => o.id === scenarioTarget);
        return `Outflow${oc ? ` "${oc.category}"` : ''} ${dayStr}`;
      }
      return `${scenarioType} ${dayStr}`;
    })();
    setStagedScenarios((prev) => [
      ...prev,
      { ...buildScenarioPayload(), label, _id: `${Date.now()}-${Math.random()}` },
    ]);
    // Reset form for next scenario
    setScenarioDays(0);
    setScenarioTarget('');
    setScenarioTargetIds([]);
    setScenarioPreview(null);
  };

  const handleRemoveStaged = (id) => {
    setStagedScenarios((prev) => prev.filter((s) => s._id !== id));
    setScenarioPreview(null);
  };

  // Combined preview — sum cash shifts across all staged scenarios
  const handlePreviewCombined = async () => {
    if (stagedScenarios.length === 0) {
      toast.error('Stage at least one scenario first');
      return;
    }
    setScenarioLoading(true);
    try {
      const responses = await Promise.all(
        stagedScenarios.map((s) =>
          axios.post(`${API}/scenario/preview`, { ...s, is_preview: true })
        )
      );
      // Aggregate
      const agg = {
        affected_invoices: 0,
        affected_bills: 0,
        affected_other_cash: 0,
        total_cash_shift: 0,
        cash_position_change: 0,
        amount_shifted: 0,
        days_shifted: 0,
        monthly_changes: [],
      };
      const monthIndex = {};
      let daysWeightedNumer = 0;
      let daysWeightedDenom = 0;
      responses.forEach((r) => {
        const d = r.data || {};
        agg.affected_invoices += d.affected_invoices || 0;
        agg.affected_bills += d.affected_bills || 0;
        agg.affected_other_cash += d.affected_other_cash || 0;
        agg.total_cash_shift += d.total_cash_shift || 0;
        agg.cash_position_change += d.cash_position_change || 0;
        agg.amount_shifted += d.amount_shifted || 0;
        if ((d.amount_shifted || 0) > 0 && d.days_shifted != null) {
          daysWeightedNumer += Math.abs(d.days_shifted) * (d.amount_shifted || 0);
          daysWeightedDenom += d.amount_shifted || 0;
        }
        (d.monthly_changes || []).forEach((mc) => {
          const key = `${mc.year}-${mc.month}`;
          if (!monthIndex[key]) {
            monthIndex[key] = { ...mc };
          } else {
            monthIndex[key].net_change = (monthIndex[key].net_change || 0) + (mc.net_change || 0);
            monthIndex[key].ar_change = (monthIndex[key].ar_change || 0) + (mc.ar_change || 0);
            monthIndex[key].ap_change = (monthIndex[key].ap_change || 0) + (mc.ap_change || 0);
          }
        });
      });
      agg.days_shifted = daysWeightedDenom > 0 ? Math.round(daysWeightedNumer / daysWeightedDenom) : 0;
      agg.monthly_changes = Object.values(monthIndex).filter((m) => Math.abs(m.net_change || 0) > 0.01);
      setScenarioPreview(agg);
    } catch (error) {
      toast.error('Failed to preview combined scenarios');
    } finally {
      setScenarioLoading(false);
    }
  };

  // Apply all staged scenarios sequentially
  const handleApplyAllStaged = async () => {
    if (stagedScenarios.length === 0) return;
    setScenarioLoading(true);
    try {
      for (const s of stagedScenarios) {
        await axios.post(`${API}/scenario/apply`, s);
      }
      toast.success(`${stagedScenarios.length} scenario(s) applied`);
      setStagedScenarios([]);
      setScenarioPreview(null);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to apply all scenarios');
    } finally {
      setScenarioLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const formatted = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(value));
    return value < 0 ? `-${formatted}` : formatted;
  };

  // Forecast window bounds derived from the dashboard data (13-month span).
  // Returns { startISO, endISO } as YYYY-MM-DD strings.
  const getForecastWindow = () => {
    if (!data?.monthly_data?.length) return { startISO: '', endISO: '' };
    const first = data.monthly_data[0];
    const last = data.monthly_data[data.monthly_data.length - 1];
    const firstMonthNum = MONTH_NAMES.indexOf(first.month) + 1;
    const lastMonthNum = MONTH_NAMES.indexOf(last.month) + 1;
    const startDay = data.opening_balance_date
      ? data.opening_balance_date.slice(8, 10)
      : '01';
    const startISO =
      data.opening_balance_date ||
      `${first.year}-${String(firstMonthNum).padStart(2, '0')}-${startDay}`;
    // Last day of last month
    const lastDay = new Date(last.year, lastMonthNum, 0).getDate();
    const endISO = `${last.year}-${String(lastMonthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startISO, endISO };
  };

  // Fetch daily data across a date range by hitting the per-month endpoint
  // for each month covered, then stitching the days together.
  const fetchDailyRange = async (startISO, endISO) => {
    if (!startISO || !endISO) {
      toast.error('Please pick both start and end dates');
      return;
    }
    if (startISO > endISO) {
      toast.error('Start date must be on or before end date');
      return;
    }
    const { startISO: minISO, endISO: maxISO } = getForecastWindow();
    if (minISO && startISO < minISO) {
      toast.error(`Start date cannot be before ${minISO}`);
      return;
    }
    if (maxISO && endISO > maxISO) {
      toast.error(`End date cannot be after ${maxISO}`);
      return;
    }

    setDailyRangeLoading(true);
    try {
      const start = new Date(startISO + 'T00:00:00');
      const end = new Date(endISO + 'T00:00:00');
      const months = [];
      const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      const endCursor = new Date(end.getFullYear(), end.getMonth(), 1);
      while (cursor <= endCursor) {
        months.push({ year: cursor.getFullYear(), month: cursor.getMonth() + 1 });
        cursor.setMonth(cursor.getMonth() + 1);
      }
      const responses = await Promise.all(
        months.map((mm) => axios.get(`${API}/dashboard/daily/${mm.year}/${mm.month}`))
      );
      const allDays = [];
      responses.forEach((res) => {
        (res.data?.daily_data || []).forEach((d) => {
          if (d.date >= startISO && d.date <= endISO) {
            allDays.push(d);
          }
        });
      });
      allDays.sort((a, b) => a.date.localeCompare(b.date));
      setDailyRangeData(allDays);
      setDailyRangeApplied(true);
    } catch (error) {
      toast.error('Failed to load daily forecast');
      console.error(error);
    } finally {
      setDailyRangeLoading(false);
    }
  };

  // Default the daily date range to first 30 days of the forecast window
  // the first time the user switches into Daily view. Persists the user's
  // last-used range in localStorage (per business) so they don't have to
  // re-click "Apply" on every dashboard visit.
  useEffect(() => {
    if (chartView !== 'daily' || !data?.monthly_data?.length) return;
    if (chartDailyStart && chartDailyEnd) return;
    const { startISO, endISO } = getForecastWindow();
    if (!startISO) return;

    const bizId = activeBusiness?.id || 'default';
    const storageKey = `cf:daily-range:${bizId}`;
    let resolvedStart = '';
    let resolvedEnd = '';
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (saved?.start && saved?.end && saved.start >= startISO && saved.end <= endISO) {
        resolvedStart = saved.start;
        resolvedEnd = saved.end;
      }
    } catch {
      /* ignore */
    }

    if (!resolvedStart) {
      const start = new Date(startISO + 'T00:00:00');
      const defaultEnd = new Date(start);
      defaultEnd.setDate(defaultEnd.getDate() + 29);
      const max = new Date(endISO + 'T00:00:00');
      const finalEnd = defaultEnd > max ? max : defaultEnd;
      resolvedStart = startISO;
      resolvedEnd = finalEnd.toISOString().slice(0, 10);
    }
    setChartDailyStart(resolvedStart);
    setChartDailyEnd(resolvedEnd);
    // Auto-load so users don't have to click Apply on every visit
    fetchDailyRange(resolvedStart, resolvedEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartView, data, activeBusiness]);

  // Persist the user's chosen range whenever they Apply or change it
  useEffect(() => {
    if (!chartDailyStart || !chartDailyEnd || !activeBusiness?.id) return;
    try {
      localStorage.setItem(
        `cf:daily-range:${activeBusiness.id}`,
        JSON.stringify({ start: chartDailyStart, end: chartDailyEnd })
      );
    } catch {
      /* ignore quota errors */
    }
  }, [chartDailyStart, chartDailyEnd, activeBusiness?.id]);

  const getCashClass = (value) => {
    if (value > 0) return 'cash-positive';
    if (value < 0) return 'cash-negative';
    return '';
  };

  // Generate suggestions for negative months
  const getSuggestions = (month) => {
    const suggestions = [
      { 
        icon: '🏦', 
        title: 'Loan Drawdown',
        description: 'Consider drawing down on existing credit facilities or applying for short-term financing'
      },
      { 
        icon: '💰', 
        title: 'Owner Equity Injection', 
        description: 'Inject additional capital from owners/shareholders to cover the shortfall'
      },
      { 
        icon: '📥', 
        title: 'Accelerate Customer Payments', 
        description: 'Offer early payment discounts to customers or follow up on overdue invoices'
      },
      { 
        icon: '📤', 
        title: 'Delay Supplier Payments', 
        description: 'Negotiate extended payment terms with suppliers or defer non-critical payments'
      },
      { 
        icon: '📋', 
        title: 'Invoice Factoring', 
        description: 'Sell outstanding invoices to a factoring company for immediate cash'
      },
    ];
    return suggestions;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Prepare chart data with month-year labels
  const monthlyChartData = data?.monthly_data?.map(m => ({
    month: `${m.month} ${m.year.toString().slice(-2)}`,
    monthLabel: m.month,
    year: m.year,
    closing: m.closing_cash,
    positive: m.closing_cash >= 0 ? m.closing_cash : null,
    negative: m.closing_cash < 0 ? m.closing_cash : null,
    opening: m.opening_cash,
    forecast_in: m.forecast_in || 0,
    forecast_out: m.forecast_out || 0,
  })) || [];

  // Daily-range chart points (when user picks Daily view + range)
  // X-axis label uses Australian dd/MM short format (e.g. 09/05)
  const dailyChartData = dailyRangeData.map((d) => ({
    month: format(parseISO(d.date), 'dd/MM'),
    fullDate: d.date,
    closing: d.closing_cash,
    opening: d.opening_cash,
    ar_in: d.ar_in || 0,
    ap_out: d.ap_out || 0,
    other_in: d.other_in || 0,
    other_out: d.other_out || 0,
    forecast_in: d.forecast_in || 0,
    forecast_out: d.forecast_out || 0,
    net_movement: d.net_movement || 0,
  }));

  // Active chart series
  const isDailyChart = chartView === 'daily';
  const baseChartData = isDailyChart
    ? dailyChartData
    : monthlyChartData.slice(0, Math.max(1, Math.min(13, chartMonthCount)));

  // Augment with `below_threshold` value so we can shade days below the
  // user's threshold using a stacked area (clipped between threshold and the
  // actual closing balance where closing < threshold).
  const chartData = (cashflowThreshold > 0)
    ? baseChartData.map((d) => ({
        ...d,
        below_threshold: Math.min(Number(d.closing) || 0, cashflowThreshold),
      }))
    : baseChartData;

  const negativeMonths = data?.monthly_data?.filter(m => m.closing_cash < 0) || [];
  const hasNegativeCash = negativeMonths.length > 0;
  
  // Calculate Y-axis domain to always show $0 line
  const allValues = chartData.map(d => d.closing);
  const minValue = Math.min(...allValues, 0);
  const maxValue = Math.max(...allValues, 0);
  const padding = Math.max(Math.abs(maxValue - minValue) * 0.1, 1000);

  // Get period description
  const getPeriodDescription = () => {
    if (!data) return '';
    if (data.opening_balance_date) {
      const obd = new Date(data.opening_balance_date + 'T00:00:00');
      const day = obd.getDate();
      const monthName = MONTH_NAMES[obd.getMonth()];
      const year = obd.getFullYear();
      const endMonthNum = ((data.start_month - 1 + 12) % 12) + 1;
      const endYear = data.start_year + 1;
      const endMonth = MONTH_NAMES[endMonthNum - 1];
      return `${day} ${monthName} ${year} - ${endMonth} ${endYear}`;
    }
    const startMonth = MONTH_NAMES[data.start_month - 1];
    const endMonthNum = ((data.start_month - 1 + 12) % 12) + 1;
    const endYear = data.start_year + 1;
    const endMonth = MONTH_NAMES[endMonthNum - 1];
    return `${startMonth} ${data.start_year} - ${endMonth} ${endYear}`;
  };

  const getOpeningLabel = () => {
    if (!data) return 'Opening Cash';
    if (data.opening_balance_date) {
      const obd = new Date(data.opening_balance_date + 'T00:00:00');
      const day = obd.getDate();
      const monthName = MONTH_NAMES[obd.getMonth()];
      return `Opening Cash (${day} ${monthName})`;
    }
    return `Opening Cash (${MONTH_NAMES[data.start_month - 1]})`;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              {data?.business_logo && (
                <img src={data.business_logo} alt="" className="w-8 h-8 object-contain rounded" />
              )}
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {data?.business_name || 'Cash Flow Dashboard'}
              </h1>
            </div>
            <p className="text-slate-500 mt-1">
              {getPeriodDescription()} (13 months)
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Switch
                id="include-other"
                checked={includeOther}
                onCheckedChange={setIncludeOther}
                data-testid="include-other-switch"
              />
              <Label htmlFor="include-other" className="text-sm text-slate-600">
                Include Other Cash
              </Label>
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                await Promise.all([
                  fetchDashboard(),
                  fetchOverdue(),
                  fetchNegativeDays(),
                  chartDailyStart && chartDailyEnd
                    ? fetchDailyRange(chartDailyStart, chartDailyEnd)
                    : Promise.resolve(),
                ]);
              }}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            {xeroStatus?.connected && !activeBusiness?.is_consolidated && (
              <div className="flex flex-col items-stretch gap-0.5">
                <Button
                  onClick={handleXeroSync}
                  disabled={xeroSyncing}
                  className="gap-2 text-white border-0 shadow-sm hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#13B5EA' }}
                  data-testid="dashboard-xero-sync-btn"
                  title="Sync invoices, bills and bank balances from Xero"
                >
                  {xeroSyncing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Sync Xero
                </Button>
                <span
                  className="text-[10px] text-slate-500 dark:text-slate-400 text-center leading-tight"
                  data-testid="xero-last-sync-label"
                  title={xeroStatus.last_sync || ''}
                >
                  {xeroStatus.last_sync
                    ? `Last synced ${formatRelativeTime(xeroStatus.last_sync)}`
                    : 'Never synced'}
                </span>
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => openExportModal('csv')}
              className="gap-2"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              CSV
            </Button>
            <Button
              onClick={() => openExportModal('excel')}
              className="gap-2 bg-primary hover:bg-primary/90"
              data-testid="export-excel-btn"
            >
              <Download className="w-4 h-4" />
              Excel
            </Button>
          </div>
        </div>

        {/* Two-column premium layout: main content + sticky bank sidebar */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-6 min-w-0">

        {/* (Bank Accounts panel moved into right sidebar below) */}

        {/* Consolidated-mode banner (read-only) */}
        {activeBusiness?.is_consolidated && (
          <div
            className="rounded-lg border border-indigo-200 bg-indigo-50/70 dark:bg-indigo-900/20 dark:border-indigo-800 px-4 py-3 flex items-center justify-between"
            data-testid="consolidated-banner"
          >
            <div className="flex items-start gap-3 text-sm">
              <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-indigo-900 dark:text-indigo-100">
                  Consolidated view
                </p>
                <p className="text-indigo-700 dark:text-indigo-300 text-xs mt-0.5">
                  Showing combined cash flow across {data?.totals?.consolidated_business_count || 0} businesses.
                  Edit actions, Xero sync, invoices, bills and scenarios are read-only here —
                  switch to a specific business to make changes.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Overdue items warning — shown when any invoice or bill has an expected_payment_date before today */}
        <OverdueWarningCard
          overdue={overdue}
          onResolved={async () => {
            await Promise.all([
              fetchDashboard(),
              fetchOverdue(),
              fetchNegativeDays(),
              fetchScenarioData(),
              // Refetch the persistent Daily Range table so rescheduled items
              // appear immediately (otherwise dailyRangeData is stale).
              chartDailyStart && chartDailyEnd
                ? fetchDailyRange(chartDailyStart, chartDailyEnd)
                : Promise.resolve(),
            ]);
          }}
        />

        {/* Payment cycle averages — DSO / DPO / Gross Margin from last 365 days of Xero data */}
        <PaymentStatsCard stats={paymentStats} onUpdated={fetchPaymentStats} />

        {/* Scenario Testing Panel — premium card */}
        {/* Chart */}
        <Card>
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Forecasted Daily Cash Balance
              </CardTitle>
              <div
                className="flex flex-col sm:flex-row sm:items-center gap-3 lg:gap-4"
                data-testid="chart-range-controls"
              >
                {/* Daily: date range */}
                {chartView === 'daily' && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Label className="text-xs text-slate-600 whitespace-nowrap">From</Label>
                    <Input
                      type="date"
                      value={chartDailyStart}
                      min={getForecastWindow().startISO}
                      max={getForecastWindow().endISO}
                      onChange={(e) => setChartDailyStart(e.target.value)}
                      className="h-8 w-36"
                      data-testid="chart-daily-start"
                    />
                    <Label className="text-xs text-slate-600 whitespace-nowrap">To</Label>
                    <Input
                      type="date"
                      value={chartDailyEnd}
                      min={getForecastWindow().startISO}
                      max={getForecastWindow().endISO}
                      onChange={(e) => setChartDailyEnd(e.target.value)}
                      className="h-8 w-36"
                      data-testid="chart-daily-end"
                    />
                    <Button
                      size="sm"
                      onClick={() => fetchDailyRange(chartDailyStart, chartDailyEnd)}
                      disabled={dailyRangeLoading}
                      className="h-8 gap-1.5"
                      data-testid="chart-daily-apply-btn"
                    >
                      {dailyRangeLoading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Play className="w-3.5 h-3.5" />
                      )}
                      Apply
                    </Button>

                    {/* Cash-flow threshold */}
                    <div
                      className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200 dark:border-slate-700"
                      data-testid="cashflow-threshold-controls"
                    >
                      <Label
                        className="text-xs text-slate-600 whitespace-nowrap"
                        title="Minimum cash level you want to stay above. Shown as a grey line on the chart."
                      >
                        Threshold
                      </Label>
                      <Input
                        type="number"
                        step="100"
                        placeholder="0"
                        value={thresholdDraft}
                        onChange={(e) => setThresholdDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveThreshold(); }}
                        className="h-8 w-28"
                        data-testid="cashflow-threshold-input"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={saveThreshold}
                        disabled={thresholdSaving || String(cashflowThreshold) === thresholdDraft}
                        className="h-8 px-2"
                        title="Save threshold"
                        data-testid="cashflow-threshold-save-btn"
                      >
                        {thresholdSaving ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          'Set'
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="closing-cash-chart">
              {isDailyChart && !dailyRangeApplied ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-6 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                  <CalendarDays className="w-8 h-8 text-slate-300 mb-2" />
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    Pick a date range and click <span className="text-primary">Apply</span> to load the daily forecast.
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Range must fall within your 13-month forecast window.
                  </p>
                </div>
              ) : isDailyChart && dailyRangeData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  No daily activity in the selected range.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1e3a8a" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#1e3a8a" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorNegative" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="thresholdRisk" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.0}/>
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0.22}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis 
                    stroke="#64748b" 
                    fontSize={12}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    domain={[minValue - padding, maxValue + padding]}
                  />
                  <RechartsTooltip
                    content={(props) => {
                      const { active, payload, label } = props || {};
                      if (!active || !payload?.length) return null;
                      const point = payload[0]?.payload || {};
                      const fullDate = point.fullDate;
                      const headerLabel = isDailyChart && fullDate
                        ? format(parseISO(fullDate), 'EEE dd MMM yyyy')
                        : label;
                      const closing = point.closing ?? 0;
                      const opening = point.opening ?? 0;
                      const arIn = point.ar_in || 0;
                      const apOut = point.ap_out || 0;
                      const otherIn = point.other_in || 0;
                      const otherOut = point.other_out || 0;
                      const forecastIn = point.forecast_in || 0;
                      const forecastOut = point.forecast_out || 0;
                      const showBreakdown = isDailyChart && (arIn || apOut || otherIn || otherOut || forecastIn || forecastOut);
                      return (
                        <div
                          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg px-3 py-2.5 text-xs"
                          style={{ fontFamily: 'JetBrains Mono, monospace' }}
                          data-testid="chart-rich-tooltip"
                        >
                          <p className="font-bold text-slate-900 dark:text-slate-100 mb-1.5">{headerLabel}</p>
                          <div className="space-y-1">
                            <div className="flex items-center justify-between gap-4">
                              <span className="text-slate-500">Opening</span>
                              <span className={opening < 0 ? 'text-red-600 font-medium' : 'font-medium'}>{formatCurrency(opening)}</span>
                            </div>
                            {showBreakdown && (
                              <>
                                {arIn > 0 && (
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="text-emerald-600 flex items-center gap-1">
                                      <ArrowDownRight className="w-3 h-3" /> AR in
                                    </span>
                                    <span className="text-emerald-600 font-medium">+{formatCurrency(arIn)}</span>
                                  </div>
                                )}
                                {apOut > 0 && (
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="text-red-600 flex items-center gap-1">
                                      <ArrowUpRight className="w-3 h-3" /> AP out
                                    </span>
                                    <span className="text-red-600 font-medium">−{formatCurrency(apOut)}</span>
                                  </div>
                                )}
                                {otherIn > 0 && (
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="text-emerald-600 flex items-center gap-1">
                                      <ArrowDownRight className="w-3 h-3" /> Other in
                                    </span>
                                    <span className="text-emerald-600 font-medium">+{formatCurrency(otherIn)}</span>
                                  </div>
                                )}
                                {otherOut > 0 && (
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="text-red-600 flex items-center gap-1">
                                      <ArrowUpRight className="w-3 h-3" /> Other out
                                    </span>
                                    <span className="text-red-600 font-medium">−{formatCurrency(otherOut)}</span>
                                  </div>
                                )}
                                {forecastIn > 0 && (
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="text-emerald-500/80 flex items-center gap-1">
                                      <ArrowDownRight className="w-3 h-3" /> Forecast in
                                    </span>
                                    <span className="text-emerald-500/80 font-medium">+{formatCurrency(forecastIn)}</span>
                                  </div>
                                )}
                                {forecastOut > 0 && (
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="text-red-500/80 flex items-center gap-1">
                                      <ArrowUpRight className="w-3 h-3" /> Forecast COGS
                                    </span>
                                    <span className="text-red-500/80 font-medium">−{formatCurrency(forecastOut)}</span>
                                  </div>
                                )}
                              </>
                            )}
                            <div className="flex items-center justify-between gap-4 pt-1 mt-1 border-t border-slate-200 dark:border-slate-700">
                              <span className="font-bold text-slate-700 dark:text-slate-200">Closing</span>
                              <span className={`font-bold ${closing < 0 ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                {formatCurrency(closing)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  {/* Always show $0 reference line */}
                  <ReferenceLine 
                    y={0} 
                    stroke="#ef4444" 
                    strokeWidth={1.5}
                    strokeDasharray="6 4"
                  />
                  {/* Cash-flow threshold line (only when > 0) */}
                  {cashflowThreshold > 0 && (
                    <ReferenceLine
                      y={cashflowThreshold}
                      stroke="#64748b"
                      strokeWidth={1.5}
                      strokeDasharray="6 4"
                      ifOverflow="extendDomain"
                      label={{
                        value: `Threshold $${cashflowThreshold.toLocaleString()}`,
                        position: 'insideTopRight',
                        fill: '#475569',
                        fontSize: 11,
                      }}
                    />
                  )}
                  {/* Area fill for visual effect - hidden from tooltip */}
                  <Area 
                    type="monotone" 
                    dataKey="closing" 
                    stroke="none"
                    fill="url(#colorPositive)"
                    tooltipType="none"
                  />
                  {/* Risk-zone: soft red shading where closing < threshold */}
                  {cashflowThreshold > 0 && (
                    <Area
                      type="monotone"
                      dataKey="below_threshold"
                      baseValue={cashflowThreshold}
                      stroke="none"
                      fill="url(#thresholdRisk)"
                      isAnimationActive={false}
                      activeDot={false}
                      tooltipType="none"
                    />
                  )}
                  {/* Main line - using segments to show red below zero */}
                  <Line 
                    type="monotone" 
                    dataKey="closing" 
                    stroke="#1e3a8a" 
                    strokeWidth={3}
                    dot={<CustomDot />}
                    activeDot={{ r: 7, fill: '#3b82f6', stroke: 'white', strokeWidth: 2 }}
                    strokeLinecap="round"
                  />
                </ComposedChart>
              </ResponsiveContainer>
              )}
            </div>
            <div className="flex items-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-1 bg-primary rounded"></div>
                <span className="text-slate-600">Positive Cash</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-1 bg-red-500 rounded"></div>
                <span className="text-slate-600">Negative Cash</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-red-500 rounded" style={{ borderStyle: 'dashed' }}></div>
                <span className="text-slate-600">$0 Line</span>
              </div>
              {cashflowThreshold > 0 && (
                <div className="flex items-center gap-2" data-testid="chart-threshold-legend">
                  <div className="w-4 h-0.5 bg-slate-500 rounded" style={{ borderStyle: 'dashed' }}></div>
                  <span className="text-slate-600">Threshold (${cashflowThreshold.toLocaleString()})</span>
                </div>
              )}
              {cashflowThreshold > 0 && (
                <div className="flex items-center gap-2" data-testid="chart-risk-zone-legend">
                  <div className="w-4 h-2.5 rounded" style={{ background: 'rgba(239,68,68,0.22)' }}></div>
                  <span className="text-slate-600">Below threshold</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cash Flow Tables — collapsed by default. Show on demand via dropdown */}
        <Card data-testid="cashflow-tables-card">
          <CardHeader className="cursor-pointer select-none" onClick={() => setTablesOpen(o => !o)}>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Cash Flow Tables
                </CardTitle>
                <CardDescription>
                  {tablesOpen
                    ? 'Switch between monthly and daily breakdown below'
                    : 'Detailed monthly & daily numbers — click to expand'}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={(e) => { e.stopPropagation(); setTablesOpen(o => !o); }}
                data-testid="toggle-cashflow-tables-btn"
              >
                {tablesOpen ? 'Hide' : 'Show'}
                <ChevronDown className={`w-4 h-4 transition-transform ${tablesOpen ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </CardHeader>

          {tablesOpen && (
            <CardContent className="space-y-4">
              {/* View selector */}
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">View</Label>
                <Select value={tablesView} onValueChange={setTablesView}>
                  <SelectTrigger className="w-[220px] h-9" data-testid="tables-view-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly" data-testid="tables-view-monthly">Monthly summary (13 months)</SelectItem>
                    <SelectItem value="daily" data-testid="tables-view-daily">Daily breakdown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Monthly view */}
              {tablesView === 'monthly' && (
                <div data-testid="tables-view-monthly-panel">
                  <div className="overflow-x-auto rounded-md border border-slate-200">
                    <Table data-testid="monthly-table">
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Month</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Opening</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">AR In</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">AP Out</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Other In</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Other Out</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right" title="Net forecasted sales receipts (after net-of-Xero deduplication)">Fc. In</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right" title="Forecasted COGS payments based on gross margin">Fc. Out</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Net</TableHead>
                          <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Closing</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data?.monthly_data?.map((month, idx) => (
                          <TableRow 
                            key={`${month.month}-${month.year}`}
                            className={`cursor-pointer hover:bg-slate-100 transition-colors ${month.closing_cash < 0 ? 'bg-red-50 hover:bg-red-100' : ''}`}
                            data-testid={`month-row-${month.month}-${month.year}`}
                            onDoubleClick={() => handleMonthDoubleClick(month)}
                            title="Double-click to view daily breakdown"
                          >
                            <TableCell className="font-medium text-slate-900">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span>{month.month} {month.year}</span>
                                {month.closing_cash < 0 && (
                                  <AlertTriangle className="w-4 h-4 text-red-500" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {formatCurrency(month.opening_cash)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm cash-positive">
                              {formatCurrency(month.ar_in)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm cash-negative">
                              {formatCurrency(month.ap_out)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm cash-positive">
                              {formatCurrency(month.other_in)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm cash-negative">
                              {formatCurrency(month.other_out)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-emerald-500/90">
                              {month.forecast_in ? (
                                (() => {
                                  const dedup = data?.forecast_dedup_by_month?.[`${month.year}-${String(month.month_num).padStart(2,'0')}`];
                                  const cell = (
                                    <span data-testid={`fc-in-${month.month_key}`}>{formatCurrency(month.forecast_in)}</span>
                                  );
                                  if (!dedup || dedup.forecast_gross === 0) return cell;
                                  return (
                                    <TooltipProvider delayDuration={120}>
                                      <TooltipUI>
                                        <TooltipTrigger asChild>
                                          <span className="underline decoration-dotted decoration-emerald-300 cursor-help">
                                            {formatCurrency(month.forecast_in)}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="text-xs max-w-xs">
                                          <p className="font-semibold mb-1.5">Forecast — Net of Xero</p>
                                          <div className="space-y-0.5 font-mono">
                                            <div className="flex justify-between gap-4"><span>Gross forecast</span><span>{formatCurrency(dedup.forecast_gross)}</span></div>
                                            <div className="flex justify-between gap-4 text-red-300"><span>− Xero raised</span><span>{formatCurrency(dedup.xero_raised)}</span></div>
                                            <div className="flex justify-between gap-4 text-red-300"><span>= Suppressed</span><span>{formatCurrency(dedup.forecast_suppressed)}</span></div>
                                            <div className="border-t border-white/20 my-1"></div>
                                            <div className="flex justify-between gap-4 font-semibold"><span>Applied</span><span>{formatCurrency(dedup.forecast_applied)}</span></div>
                                            <div className="text-[10px] text-slate-300 mt-1">
                                              Kept {(dedup.keep_ratio * 100).toFixed(0)}% of the forecast for this month.
                                            </div>
                                          </div>
                                        </TooltipContent>
                                      </TooltipUI>
                                    </TooltipProvider>
                                  );
                                })()
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-red-500/90">
                              {month.forecast_out ? formatCurrency(month.forecast_out) : '—'}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-sm font-medium ${getCashClass(month.net_movement)}`}>
                              {formatCurrency(month.net_movement)}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-sm font-bold ${getCashClass(month.closing_cash)}`}>
                              {formatCurrency(month.closing_cash)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    Double-click on any month row to view daily cash flow breakdown
                  </p>
                </div>
              )}

              {/* Daily view */}
              {tablesView === 'daily' && (
                <div data-testid="tables-view-daily-panel">
                  {!isDailyChart || !dailyRangeApplied || dailyRangeData.length === 0 ? (
                    <div className="text-center py-10 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">
                      Apply a date range on the chart above to see the daily breakdown.
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-slate-500 mb-2">
                        {chartDailyStart ? format(parseISO(chartDailyStart), 'dd MMM yyyy') : ''} — {chartDailyEnd ? format(parseISO(chartDailyEnd), 'dd MMM yyyy') : ''}
                        <span className="ml-2 text-xs text-slate-400">({dailyRangeData.length} day{dailyRangeData.length === 1 ? '' : 's'})</span>
                      </div>
                      <div className="overflow-x-auto rounded-md border border-slate-200 max-h-[480px] overflow-y-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-slate-50 z-10">
                            <TableRow>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Opening</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">AR In</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">AP Out</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Other In</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Other Out</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right" title="Forecasted sales receipts (net of Xero invoices raised in the same month)">Fc. In</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right" title="Forecasted COGS payments based on gross margin">Fc. Out</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Net</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Closing</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {dailyRangeData.map((d) => (
                              <TableRow key={d.date} data-testid={`daily-row-${d.date}`}>
                                <TableCell className="font-mono text-xs">{format(parseISO(d.date), 'dd MMM yyyy')}</TableCell>
                                <TableCell className={`font-mono text-right ${getCashClass(d.opening_cash)}`}>{formatCurrency(d.opening_cash)}</TableCell>
                                <TableCell className="font-mono text-right text-emerald-600">{d.ar_in ? formatCurrency(d.ar_in) : '—'}</TableCell>
                                <TableCell className="font-mono text-right text-red-600">{d.ap_out ? formatCurrency(d.ap_out) : '—'}</TableCell>
                                <TableCell className="font-mono text-right text-emerald-600">{d.other_in ? formatCurrency(d.other_in) : '—'}</TableCell>
                                <TableCell className="font-mono text-right text-red-600">{d.other_out ? formatCurrency(d.other_out) : '—'}</TableCell>
                                <TableCell className="font-mono text-right text-emerald-600/80">{d.forecast_in ? formatCurrency(d.forecast_in) : '—'}</TableCell>
                                <TableCell className="font-mono text-right text-red-600/80">{d.forecast_out ? formatCurrency(d.forecast_out) : '—'}</TableCell>
                                <TableCell className={`font-mono text-right ${getCashClass(d.net_movement)}`}>{formatCurrency(d.net_movement)}</TableCell>
                                <TableCell className={`font-mono text-right font-semibold ${getCashClass(d.closing_cash)}`}>{formatCurrency(d.closing_cash)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Cash Flow Alert — compact 1-line summary of negative days in next 90 days */}
        {negativeDays.length > 0 && (
          <Card className="border-red-200 bg-red-50/40" data-testid="cash-flow-alert-compact">
            <CardContent className="py-3 px-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                <span className="font-semibold text-red-700 dark:text-red-300">
                  Negative cash on
                </span>
                <span className="text-red-700/80 dark:text-red-300/80">
                  ({negativeDays.length} {negativeDays.length === 1 ? 'day' : 'days'} in next 90):
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {negativeDays.slice(0, 10).map((d) => (
                    <span
                      key={d.date}
                      className="inline-flex items-center px-2 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-full text-[11px] font-medium font-mono"
                      title={`Closing: ${formatCurrency(d.closing_cash)}`}
                      data-testid={`negative-day-${d.date}`}
                    >
                      {format(parseISO(d.date), 'dd MMM')}
                    </span>
                  ))}
                  {negativeDays.length > 10 && (
                    <span className="inline-flex items-center px-2 py-0.5 bg-red-100/60 text-red-700 dark:text-red-300 rounded-full text-[11px]">
                      +{negativeDays.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly Data Table — moved into the collapsible "Cash Flow Tables" card above */}

          </div>{/* /main content column */}

          {/* Right sticky sidebar — Bank Accounts (Xero-driven) */}
          <aside className="xl:sticky xl:top-6 xl:self-start space-y-4" data-testid="bank-sidebar">
            <BankAccountsPanel
              activeBusiness={activeBusiness}
              onSaved={fetchDashboard}
              compact
            />
          </aside>
        </div>{/* /two-column grid */}

        {/* Daily Cash Flow Modal */}
        <Dialog open={dailyModalOpen} onOpenChange={setDailyModalOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Calendar className="w-5 h-5 text-primary" />
                Daily Cash Flow - {selectedMonth?.name} {selectedMonth?.year}
              </DialogTitle>
            </DialogHeader>
            
            {dailyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : dailyData ? (
              <TooltipProvider>
              <div className="flex-1 overflow-auto space-y-4">
                {/* Daily Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase">Opening</p>
                    <p className="text-lg font-bold font-mono">{formatCurrency(dailyData.opening_cash)}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 uppercase">Total AR In</p>
                    <p className="text-lg font-bold font-mono text-green-600">{formatCurrency(dailyData.totals.ar_in)}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-xs text-red-600 uppercase">Total AP Out</p>
                    <p className="text-lg font-bold font-mono text-red-600">{formatCurrency(dailyData.totals.ap_out)}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3" data-testid="daily-other-in-card">
                    <p className="text-xs text-emerald-600 uppercase">Other Cash In</p>
                    <p className="text-lg font-bold font-mono text-emerald-600">{formatCurrency(dailyData.totals.other_in || 0)}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3" data-testid="daily-other-out-card">
                    <p className="text-xs text-orange-600 uppercase">Other Cash Out</p>
                    <p className="text-lg font-bold font-mono text-orange-600">{formatCurrency(dailyData.totals.other_out || 0)}</p>
                  </div>
                  <div className="bg-emerald-50/60 rounded-lg p-3 border border-emerald-200/60" data-testid="daily-forecast-in-card">
                    <p className="text-xs text-emerald-700 uppercase">Forecast In</p>
                    <p className="text-lg font-bold font-mono text-emerald-700">{formatCurrency(dailyData.totals.forecast_in || 0)}</p>
                  </div>
                  <div className="bg-red-50/60 rounded-lg p-3 border border-red-200/60" data-testid="daily-forecast-out-card">
                    <p className="text-xs text-red-700 uppercase">Forecast Out</p>
                    <p className="text-lg font-bold font-mono text-red-700">{formatCurrency(dailyData.totals.forecast_out || 0)}</p>
                  </div>
                  <div className="bg-primary/10 rounded-lg p-3">
                    <p className="text-xs text-primary uppercase">Month End</p>
                    <p className={`text-lg font-bold font-mono ${dailyData.daily_data[dailyData.daily_data.length - 1]?.closing_cash >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
                      {formatCurrency(dailyData.daily_data[dailyData.daily_data.length - 1]?.closing_cash || 0)}
                    </p>
                  </div>
                </div>

                {/* Daily Chart */}
                <div className="h-48 bg-slate-50 rounded-lg p-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={dailyData.daily_data} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" stroke="#64748b" fontSize={10} />
                      <YAxis 
                        stroke="#64748b" 
                        fontSize={10}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                      <Bar dataKey="ar_in" fill="#22c55e" name="AR In" stackId="positive" />
                      <Bar dataKey="other_in" fill="#3b82f6" name="Other In" stackId="positive" />
                      <Bar dataKey="ap_out" fill="#ef4444" name="AP Out" stackId="negative" />
                      <Bar dataKey="other_out" fill="#f97316" name="Other Out" stackId="negative" />
                      <Line type="monotone" dataKey="closing_cash" stroke="#1e3a8a" strokeWidth={2} dot={false} name="Closing" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Daily Data Table */}
                <div className="border rounded-lg overflow-auto max-h-[300px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-semibold">Day</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Opening</TableHead>
                        <TableHead className="text-xs font-semibold text-right">AR In</TableHead>
                        <TableHead className="text-xs font-semibold text-right">AP Out</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Other In</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Other Out</TableHead>
                        <TableHead className="text-xs font-semibold text-right" title="Forecasted sales receipts (net of Xero invoices)">Fc. In</TableHead>
                        <TableHead className="text-xs font-semibold text-right" title="Forecasted COGS payments">Fc. Out</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Net</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Closing</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dailyData.daily_data.map((day) => {
                        const hasActivity = day.ar_in > 0 || day.ap_out > 0 || day.other_in > 0 || day.other_out > 0 || day.forecast_in > 0 || day.forecast_out > 0;
                        const hasTransactions = day.transactions.length > 0;
                        
                        const rowContent = (
                          <TableRow 
                            key={day.day}
                            className={`${day.closing_cash < 0 ? 'bg-red-50' : ''} ${hasActivity ? '' : 'text-slate-400'} ${hasTransactions ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                          >
                            <TableCell className="font-medium text-sm">
                              <div className="flex items-center gap-1">
                                <span>{day.day}</span>
                                {hasTransactions && (
                                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                                    {day.transactions.length}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">
                              {formatCurrency(day.opening_cash)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-green-600">
                              {day.ar_in > 0 ? formatCurrency(day.ar_in) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-red-600">
                              {day.ap_out > 0 ? formatCurrency(day.ap_out) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-green-600">
                              {day.other_in > 0 ? formatCurrency(day.other_in) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-red-600">
                              {day.other_out > 0 ? formatCurrency(day.other_out) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-emerald-600/80">
                              {day.forecast_in > 0 ? formatCurrency(day.forecast_in) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-red-600/80">
                              {day.forecast_out > 0 ? formatCurrency(day.forecast_out) : '-'}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-xs font-medium ${day.net_movement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {day.net_movement !== 0 ? formatCurrency(day.net_movement) : '-'}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-xs font-bold ${day.closing_cash >= 0 ? '' : 'text-red-600'}`}>
                              {formatCurrency(day.closing_cash)}
                            </TableCell>
                          </TableRow>
                        );
                        
                        // Wrap rows with transactions in a tooltip
                        if (hasTransactions) {
                          return (
                            <TooltipUI key={day.day} delayDuration={200}>
                              <TooltipTrigger asChild>
                                {rowContent}
                              </TooltipTrigger>
                              <TooltipContent 
                                side="right" 
                                className="bg-white border shadow-lg p-0 max-w-md"
                                sideOffset={10}
                              >
                                <div className="p-3">
                                  <p className="text-xs font-semibold text-slate-700 mb-2 border-b pb-1">
                                    Day {day.day} - {day.transactions.length} Transaction{day.transactions.length > 1 ? 's' : ''}
                                  </p>
                                  <div className="space-y-1.5 max-h-48 overflow-auto">
                                    {day.transactions.map((t, idx) => (
                                      <div key={idx} className="flex items-center justify-between gap-4 text-xs">
                                        <div className="flex items-center gap-2">
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            t.type === 'AR' ? 'bg-green-100 text-green-700' :
                                            t.type === 'AP' ? 'bg-red-100 text-red-700' :
                                            t.type === 'Other In' ? 'bg-blue-100 text-blue-700' :
                                            'bg-orange-100 text-orange-700'
                                          }`}>
                                            {t.type}
                                          </span>
                                          <span className="text-slate-700 font-medium">{t.counterparty}</span>
                                          {t.description && (
                                            <span className="text-slate-400 truncate max-w-[150px]">{t.description}</span>
                                          )}
                                        </div>
                                        <span className={`font-mono font-semibold whitespace-nowrap ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {formatCurrency(t.amount)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TooltipContent>
                            </TooltipUI>
                          );
                        }
                        
                        return rowContent;
                      })}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  💡 Hover over days with transactions to see details
                </p>
              </div>
              </TooltipProvider>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Export Modal */}
        <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                Export Cash Flow Data
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Export Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">What would you like to export?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setExportType('monthly')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      exportType === 'monthly' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <CalendarDays className={`w-6 h-6 mb-2 ${exportType === 'monthly' ? 'text-primary' : 'text-slate-400'}`} />
                    <p className="font-semibold text-sm">Monthly Summary</p>
                    <p className="text-xs text-slate-500 mt-1">Full 13-month forecast overview</p>
                  </button>
                  <button
                    onClick={() => setExportType('daily')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      exportType === 'daily' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Calendar className={`w-6 h-6 mb-2 ${exportType === 'daily' ? 'text-primary' : 'text-slate-400'}`} />
                    <p className="font-semibold text-sm">Daily Breakdown</p>
                    <p className="text-xs text-slate-500 mt-1">Day-by-day cash flow details</p>
                  </button>
                </div>
              </div>

              {/* Month Selection (only for daily export) */}
              {exportType === 'daily' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Select months to include:</Label>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={toggleAllMonths}
                      className="text-xs h-7"
                    >
                      <CheckSquare className="w-3 h-3 mr-1" />
                      {selectedMonths.length === data?.monthly_data?.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-auto p-1">
                    {data?.monthly_data?.map((month) => {
                      const monthKey = `${month.year}-${month.month_num}`;
                      const isSelected = selectedMonths.includes(monthKey);
                      return (
                        <label
                          key={monthKey}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-primary bg-primary/5' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleMonthSelection(monthKey)}
                          />
                          <span className="text-sm">
                            {month.month} {month.year.toString().slice(-2)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {selectedMonths.length === 0 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Please select at least one month
                    </p>
                  )}
                </div>
              )}

              {/* Format indicator */}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Download className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">
                  Export as: <strong>{exportFormat === 'excel' ? 'Excel (.xlsx)' : 'CSV (.csv)'}</strong>
                </span>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setExportModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleExport}
                disabled={exporting || (exportType === 'daily' && selectedMonths.length === 0)}
                className="gap-2"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export {exportFormat === 'excel' ? 'Excel' : 'CSV'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
